/**
 * Tri et filtres de la liste de la Médiathèque. Fonctions pures : le composant
 * ne fait que tenir l'état et afficher le résultat.
 *
 * Les dossiers restent toujours en tête, comme dans un explorateur de fichiers :
 * un tri par taille ou par date ne doit pas noyer la navigation au milieu des
 * fichiers. Une valeur inconnue (durée non lue, date absente) part toujours en
 * fin de liste, quel que soit le sens.
 */
import type { MediaBrowseEntry } from '../../lib/api.js'

export type MediaSortKey = 'name' | 'duration' | 'size' | 'modified' | 'state'
export type MediaSortDir = 'asc' | 'desc'
export interface MediaSort { key: MediaSortKey; dir: MediaSortDir }

/** Ordre du serveur : dossiers puis nom A → Z. */
export const DEFAULT_MEDIA_SORT: MediaSort = { key: 'name', dir: 'asc' }

const SORT_KEYS: readonly MediaSortKey[] = ['name', 'duration', 'size', 'modified', 'state']

/**
 * Sens proposé au premier clic sur une colonne : le nom de A à Z, tout le
 * reste du plus grand au plus petit — le plus récent, le plus long, le plus
 * lourd, puis les échecs avant les fichiers sains.
 */
export function defaultDirFor(key: MediaSortKey): MediaSortDir {
  return key === 'name' ? 'asc' : 'desc'
}

/** Clic sur une colonne : même colonne → on inverse ; autre colonne → son sens par défaut. */
export function nextSort(current: MediaSort, key: MediaSortKey): MediaSort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  return { key, dir: defaultDirFor(key) }
}

/** Relit un tri mémorisé (`localStorage`) ; tout ce qui est illisible retombe sur le tri par défaut. */
export function parseSort(raw: string | null | undefined): MediaSort {
  if (!raw) return DEFAULT_MEDIA_SORT
  const [key, dir] = raw.split(':')
  if (!SORT_KEYS.includes(key as MediaSortKey) || (dir !== 'asc' && dir !== 'desc')) return DEFAULT_MEDIA_SORT
  return { key: key as MediaSortKey, dir }
}

export function serializeSort(sort: MediaSort): string {
  return `${sort.key}:${sort.dir}`
}

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' })

/** Rang d'état : plus il est haut, plus le fichier demande d'attention. */
const STATE_RANK: Record<string, number> = { FAILED: 3, SCHEDULED: 2, CHECKED: 1 }

function timeOf(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : t
}

function valueOf(entry: MediaBrowseEntry, key: MediaSortKey, durationOf: (e: MediaBrowseEntry) => number | null): number | null {
  const isDir = entry.type === 'directory'
  switch (key) {
    case 'modified': return timeOf(entry.modifiedAt)
    case 'size':     return isDir ? (entry.itemCount ?? null) : entry.size
    case 'duration': return isDir ? null : durationOf(entry)
    case 'state':    return isDir || !entry.state ? null : STATE_RANK[entry.state] ?? null
    default:         return null
  }
}

/**
 * Trie une copie de la liste. Dossiers d'abord ; à valeur égale, le nom
 * départage (A → Z), et deux diffusions programmées passent de la plus proche
 * à la plus lointaine.
 */
export function sortMediaEntries(
  entries: readonly MediaBrowseEntry[],
  sort: MediaSort,
  durationOf: (entry: MediaBrowseEntry) => number | null = e => e.durationSec ?? null,
): MediaBrowseEntry[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  return [...entries].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1
    if (sort.key === 'name') return sign * collator.compare(a.name, b.name)

    const va = valueOf(a, sort.key, durationOf)
    const vb = valueOf(b, sort.key, durationOf)
    if (va == null || vb == null) {
      if (va != null) return -1
      if (vb != null) return 1
    } else if (va !== vb) {
      return sign * (va - vb)
    }

    if (sort.key === 'state' && a.state === 'SCHEDULED' && b.state === 'SCHEDULED') {
      const na = timeOf(a.nextBroadcastAt) ?? Infinity
      const nb = timeOf(b.nextBroadcastAt) ?? Infinity
      if (na !== nb) return na < nb ? -1 : 1
    }
    return collator.compare(a.name, b.name)
  })
}

// ─── Filtres ─────────────────────────────────────────────────────────────────

export type MediaKindFilter = 'all' | 'video' | 'image' | 'audio'
export type MediaStateFilter = 'all' | 'FAILED' | 'SCHEDULED' | 'CHECKED' | 'UNCHECKED'

export interface MediaFilter {
  query: string
  kind:  MediaKindFilter
  state: MediaStateFilter
}

export const EMPTY_MEDIA_FILTER: MediaFilter = { query: '', kind: 'all', state: 'all' }

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'svg', 'tga'])
/** Musique de l'infoservice (INFOSERVICES/MUSIQUE). */
export const AUDIO_EXT = new Set(['wav', 'mp3', 'flac', 'aac', 'm4a', 'ogg', 'aif', 'aiff'])

/** Majuscules et accents ignorés : « generique » trouve « Générique ». */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export function mediaKindOf(entry: MediaBrowseEntry): 'directory' | 'video' | 'image' | 'audio' {
  if (entry.type === 'directory') return 'directory'
  const dot = entry.name.lastIndexOf('.')
  const ext = dot > 0 ? entry.name.slice(dot + 1).toLowerCase() : ''
  if (IMAGE_EXT.has(ext)) return 'image'
  return AUDIO_EXT.has(ext) ? 'audio' : 'video'
}

export function isFilterActive(filter: MediaFilter): boolean {
  return filter.query.trim() !== '' || filter.kind !== 'all' || filter.state !== 'all'
}

/**
 * Le texte cherche dans le nom, dossiers compris (plusieurs mots : tous
 * doivent apparaître). Un filtre de type ou d'état ne vise que les fichiers :
 * un dossier n'a ni l'un ni l'autre, il est donc masqué tant qu'il est actif.
 */
export function filterMediaEntries(entries: readonly MediaBrowseEntry[], filter: MediaFilter): MediaBrowseEntry[] {
  const words = fold(filter.query).split(/\s+/).filter(Boolean)
  return entries.filter(entry => {
    if (words.length > 0) {
      const name = fold(entry.name)
      if (!words.every(w => name.includes(w))) return false
    }
    if (filter.kind === 'all' && filter.state === 'all') return true
    if (entry.type === 'directory') return false
    if (filter.kind !== 'all' && mediaKindOf(entry) !== filter.kind) return false
    if (filter.state === 'UNCHECKED') return !entry.state
    if (filter.state !== 'all') return entry.state === filter.state
    return true
  })
}
